<template>
  <div class="comments-detail relatice flex flex-col md:flex-row h-640px max-h-[calc(100dvh-128px)]">
    <div class="summery sticky shrink-0 md:w-85 px-5 py-5 top-0">
      <p class="product-name text-lg">{{ props.product.product_name }}</p>
      <div class="rating flex items-center gap-2 mt-4 select-none">
        <span class="text-4xl">{{ formattedRating }}</span>
        <svg-icon name="star" class="text-yellow-50 w-6 h-6" />
        <span class="text-base">{{ $t('review_product_review_amount', { count: props.productComments.total_count }) }}</span>
      </div>
      <div class="rating-summery select-none flex flex-col gap-1">
        <template v-for="s in scoreData" :key="s.score">
          <div class="flex items-center gap-4">
            <div class="flex justify-end items-center w-9 gap-2">
              <span>{{ s.score }}</span>
              <svg-icon name="star" class="w-4 h-4" />
            </div>
            <div class="percent flex-1">
              <n-tooltip trigger="hover">
                <template #trigger>
                  <div class="bar h-1 rounded-50px bg-yellow-50" :style="{ width: `${s.percent}%` }"></div>
                </template>
                <div class="px-4 py-2">{{ s.value }}</div>
              </n-tooltip>
            </div>
          </div>
        </template>
      </div>
    </div>
    <div class="comments w-full scrollbar">
      <div class="tags flex flex-wrap gap-2 px-5 py-5 bg-gray-30">
        <nd-button type="secondary" class="tag !py-0.5 !px-3 !text-xs" :class="{ active: !activeTag }" @click="handleTagClick('')">
          <span>{{ $t('all_all') }}</span>
          <span class="ml-1">({{ props.productComments.total_count }})</span>
        </nd-button>
        <template v-for="tag in sortedTags" :key="tag.id">
          <nd-button type="secondary" class="tag !py-0.5 !px-3 !text-xs" :class="{ active: activeTag === tag.id }" @click="handleTagClick(tag.id)">
            <span>{{ tag.name }}</span>
            <span class="ml-1">({{ tag.count }})</span>
          </nd-button>
        </template>
      </div>
      <!-- <div class="actions">
        <div class="order-by"></div>
      </div> -->
      <div class="comments-list">
        <comment-card v-for="comment in filteredComments" :key="comment.id" :comment="comment" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { NTooltip } from 'naive-ui'
import CommentCard from '@/components/comment/comment-card.vue'

import type { UiProductInfo } from '@/types/product'
import type { FreviewReviewResponse } from '@/types/api'

const props = defineProps<{
  product: UiProductInfo
  productComments: FreviewReviewResponse
}>()

const activeTag = ref<string>('')

const formattedRating = computed(() => {
  const rating = props.productComments.avg_rating
  return rating ? rating.toFixed(1) : '0.0'
})

const scoreData = computed(() => {
  const score = props.productComments.score
  if (!score) return []

  const totalCount = score.reduce((acc, c) => acc + c, 0)
  return score
    .map((count, index) => ({
      score: `${index + 1}`,
      value: count,
      percent: (count / totalCount) * 100,
    }))
    .reverse()
})

const tagsData = computed(() => {
  const tags = props.productComments.comment?.flatMap(comment => comment.tags)
  if (!tags) return []

  return tags.reduce(
    (acc, c) => {
      if (!c?.tag_id || !c?.tag_name) return acc

      const key = `${c.tag_id}`
      if (acc[key]) {
        acc[key].count += 1
      } else {
        acc[key] = {
          id: key,
          name: c.tag_name,
          count: 1,
        }
      }
      return acc
    },
    {} as Record<string, { id: string; name: string; count: number }>
  )
})

const filteredComments = computed(() => {
  const comments = props.productComments.comment || []
  if (!activeTag.value) return comments

  return comments.filter(comment => comment.tags?.some(t => `${t.tag_id}` === activeTag.value) || false)
})

const sortedTags = computed(() => {
  const tags = Object.values(tagsData.value)
  return tags.sort((a, b) => b.count - a.count)
})

const handleTagClick = (id: string) => {
  activeTag.value = id
}
</script>

<style lang="scss" scoped></style>
